import type { Bulletin } from '@shared/types/Bulletin';
import type { Salarie } from '@shared/types/Salarie';
import type { Tenant } from '@shared/types/Tenant';
import { fmtNumber, fmtFCFA, fmtDate, fmtPeriode, esc } from '@presentation/shell/format';
import { formatPayrollRate, PAYROLL_PARAMETERS } from '@shared/constants/PayrollParameters';
import { SEUIL_EXONERATION_TCS, TAUX_TCS } from '@shared/constants/Taux';
import { detailChargesPatronales } from '@domain/paie/ChargesPatronales';
import {
  brandCenterStripHtml,
  brandFontStyleBlock,
  brandFooterTextHtml,
  brandHeaderTextHtml,
  brandRootClass,
  brandRootStyleAttr,
  bulletinHeaderLogoHtml,
  bulletinRightLogoHtml,
  documentThemeFor,
} from '@presentation/print/documentBrand';
import { companyLegalFromTenant } from '@domain/legal/CompanyLegalInfo';

function anciennete(dateEmbauche: string): string {
  const d = new Date(dateEmbauche);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  let mois = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (mois < 0) mois = 0;
  const ans = Math.floor(mois / 12);
  const rm = mois % 12;
  if (ans === 0) return `${rm} mois`;
  return rm === 0 ? `${ans} an${ans > 1 ? 's' : ''}` : `${ans} an${ans > 1 ? 's' : ''} ${rm} mois`;
}

function row(label: string, base: string, taux: string, gagner: string, retenir: string): string {
  return `<tr><td>${label}</td><td class="num">${base}</td><td class="num">${taux}</td><td class="num">${gagner}</td><td class="num">${retenir}</td></tr>`;
}

function patronalRow(
  label: string,
  base: number,
  taux: number,
  montant: number,
  num: (n: number) => string,
): string {
  if (taux === 0 && montant === 0) return '';
  return `<tr><td>${label}</td><td class="num">${num(base)}</td><td class="num">${formatPayrollRate(taux)}</td><td class="num">${num(montant)}</td></tr>`;
}

/**
 * Construit le HTML d'un bulletin de paie, fidèle à la structure v2.2
 * (classes .bulletin*, portées verbatim dans nexxus-theme.css).
 */
export function buildBulletinHTML(b: Bulletin, sal: Salarie, t: Tenant | null): string {
  const num = (n: number | undefined): string => fmtNumber(n ?? 0);
  const totalRetenuesSal =
    (b.cnss_vid_sal || 0) + (b.cnamgs_sal || 0) + (b.tcs || 0) + (b.irpp_mensuel || 0);
  const brutVerse = b.brut_total ?? b.brut_imposable;
  /**
   * Base TCS affichée (Phase 4 A7) = assiette taxable déjà stockée dans `bulletin.base_tcs`
   * (= base TCS du référentiel : brut hors CNSS-seule − CNSS − CNAMGS, AVANT seuil d'exonération 150 000).
   */
  const baseTcsAffichee =
    b.base_tcs != null ? Math.max(0, b.base_tcs) : Math.max(0, Math.round((b.tcs ?? 0) / TAUX_TCS));
  const contrat = sal.statut_contrat ?? '—';
  const situation = sal.situation ?? sal.situation_familiale ?? '—';
  const enfants = sal.enfants ?? sal.nb_enfants ?? 0;
  const cnssNum = sal.cnss ?? sal.numero_cnss ?? '—';
  const cnamgsNum = sal.cnamgs ?? '—';

  const brutRows = [
    row('Salaire de base', '—', '—', num(b.brut_base ?? b.salaire_base), '—'),
    b.sursalaire ? row('Sursalaire', '—', '—', num(b.sursalaire), '—') : '',
    b.prime_anciennete ? row("Prime d'ancienneté", '—', '—', num(b.prime_anciennete), '—') : '',
    b.transport ? row('Indemnité transport', '—', '—', num(b.transport), '—') : '',
    b.logement ? row('Indemnité logement', '—', '—', num(b.logement), '—') : '',
    b.fonction_ind ? row('Indemnité fonction', '—', '—', num(b.fonction_ind), '—') : '',
    b.autres ? row('Autres indemnités', '—', '—', num(b.autres), '—') : '',
    b.avantage_nature ? row('Avantage en nature', '—', '—', num(b.avantage_nature), '—') : '',
    (b.hsup_total ?? b.heures_supplementaires)
      ? row('Heures supplémentaires', '—', '—', num(b.hsup_total ?? b.heures_supplementaires), '—')
      : '',
    b.prime ? row('Prime exceptionnelle', '—', '—', num(b.prime), '—') : '',
    b.prime_extra ? row('Prime extraordinaire', '—', '—', num(b.prime_extra), '—') : '',
    b.treizieme_mois ? row('13ᵉ mois', '—', '—', num(b.treizieme_mois), '—') : '',
  ].join('');

  const parts = (b.parts ?? b.parts_fiscales ?? 1).toFixed(1).replace('.', ',');
  const pat = detailChargesPatronales(b);
  const baseCnamgsSal = Math.min(b.brut_imposable ?? 0, PAYROLL_PARAMETERS.cnamgsCeiling);
  const legal = companyLegalFromTenant(t);
  const companyName = legal.trade_name || legal.registered_name || 'Entreprise';
  const theme = documentThemeFor(t);
  const companyMeta = [
    legal.legal_form,
    legal.nif ? `NIF ${legal.nif}` : null,
    legal.rccm ? `RCCM ${legal.rccm}` : null,
    legal.cnss_employer_id ? `CNSS emp. ${legal.cnss_employer_id}` : null,
    legal.cnamgs_id ? `CNAMGS emp. ${legal.cnamgs_id}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const companySeat = legal.headquarters || legal.phone || '';
  return `<div class="bulletin${brandRootClass(theme)}" id="bulletin-print"${brandRootStyleAttr(theme)}>
    ${brandFontStyleBlock(theme)}${brandCenterStripHtml(theme, companyName)}<div class="bulletin-header">
      <div>${bulletinHeaderLogoHtml(40, theme, companyName)}<div class="company">${esc(companyName)}</div><div class="meta">${esc(companyMeta)}${companySeat ? `<br>${esc(companySeat)}` : ''}${brandHeaderTextHtml(theme)}</div></div>
      <div style="text-align:right;">${bulletinRightLogoHtml(theme, companyName)}<div class="company">BULLETIN DE PAIE</div><div class="meta">${esc(fmtPeriode(b.periode))}</div></div>
    </div>
    <div class="bulletin-meta-section">
      <div class="group"><h4>Salarié</h4>
        <div class="row"><span class="lbl">Matricule</span><span class="val mono">${esc(sal.matricule)}</span></div>
        <div class="row"><span class="lbl">Nom &amp; prénom</span><span class="val">${esc(sal.nom)} ${esc(sal.prenom)}</span></div>
        <div class="row"><span class="lbl">Fonction</span><span class="val">${esc(sal.poste || '—')}</span></div>
        <div class="row"><span class="lbl">Contrat</span><span class="val">${esc(contrat)}</span></div>
        <div class="row"><span class="lbl">Embauche</span><span class="val">${esc(fmtDate(sal.date_embauche))}</span></div>
        <div class="row"><span class="lbl">Jours payés</span><span class="val">${esc(b.jours_travailles != null ? String(b.jours_travailles) : '—')}</span></div>
        <div class="row"><span class="lbl">Ancienneté</span><span class="val">${esc(anciennete(sal.date_embauche))}</span></div>
      </div>
      <div class="group"><h4>Immatriculations</h4>
        <div class="row"><span class="lbl">Catégorie</span><span class="val">${esc(sal.categorie)}</span></div>
        <div class="row"><span class="lbl">N° CNSS</span><span class="val mono">${esc(cnssNum)}</span></div>
        <div class="row"><span class="lbl">N° CNAMGS</span><span class="val mono">${esc(cnamgsNum)}</span></div>
        <div class="row"><span class="lbl">Situation</span><span class="val">${esc(situation)}</span></div>
        <div class="row"><span class="lbl">Enfants</span><span class="val">${esc(enfants)}</span></div>
        <div class="row"><span class="lbl">Parts fiscales</span><span class="val">${parts}</span></div>
      </div>
    </div>
    <table class="bulletin-table">
      <thead><tr><th>Désignation</th><th class="num">Base</th><th class="num">Taux</th><th class="num">À gagner</th><th class="num">À retenir</th></tr></thead>
      <tbody>
        <tr class="section-head"><td colspan="5">Éléments du brut</td></tr>
        ${brutRows}
        <tr class="total"><td><strong>Total brut versé</strong></td><td class="num">—</td><td class="num">—</td><td class="num"><strong>${num(brutVerse)}</strong></td><td class="num">—</td></tr>
        <tr class="total"><td><strong>Brut imposable</strong></td><td class="num">—</td><td class="num">—</td><td class="num"><strong>${num(b.brut_imposable)}</strong></td><td class="num">${b.exo_ss_total ? `−${num(b.exo_ss_total)} exo` : '—'}</td></tr>
        <tr class="section-head"><td colspan="5">Cotisations salariales</td></tr>
        <tr><td>CNSS Pension VID</td><td class="num">${num(b.base_cnss)}</td><td class="num">${formatPayrollRate(PAYROLL_PARAMETERS.cnssPensionEmployee)}</td><td class="num">—</td><td class="num">${num(b.cnss_vid_sal)}</td></tr>
        <tr><td>CNAMGS</td><td class="num">${num(baseCnamgsSal)}</td><td class="num">${formatPayrollRate(PAYROLL_PARAMETERS.cnamgsEmployee)}</td><td class="num">—</td><td class="num">${num(b.cnamgs_sal)}</td></tr>
        <tr><td>TCS — Base TCS = ${num(baseTcsAffichee)} (exonérée jusqu’à ${num(SEUIL_EXONERATION_TCS)})</td><td class="num">${num(baseTcsAffichee)}</td><td class="num">${formatPayrollRate(PAYROLL_PARAMETERS.tcsEmployee)}</td><td class="num">—</td><td class="num">${num(b.tcs)}</td></tr>
        <tr><td>IRPP (${parts} parts)</td><td class="num">—</td><td class="num">progressif</td><td class="num">—</td><td class="num">${num(b.irpp_mensuel)}</td></tr>
        <tr class="total"><td><strong>Total retenues salariales</strong></td><td class="num">—</td><td class="num">—</td><td class="num">—</td><td class="num"><strong>${num(totalRetenuesSal)}</strong></td></tr>
        <tr class="section-head"><td colspan="5">Imposition (détail)</td></tr>
        ${b.abattement ? `<tr><td>Abattement frais pro (mensuel)</td><td class="num">—</td><td class="num">${formatPayrollRate(PAYROLL_PARAMETERS.professionalAllowanceRate)}</td><td class="num">—</td><td class="num">${num(b.abattement)}</td></tr>` : ''}
        <tr><td>Base IRPP (mensuelle)</td><td class="num">—</td><td class="num">—</td><td class="num">${num(b.net_imposable)}</td><td class="num">—</td></tr>
        ${b.avance ? row('Avance sur salaire', '—', '—', '—', num(b.avance)) : ''}
      </tbody>
    </table>
    <div class="bulletin-net"><div class="label">Net à payer</div><div class="value"${(b.net_a_payer || 0) < 0 ? ' style="color:#C0392B;"' : ''}>${fmtFCFA(b.net_a_payer)}</div></div>
    <table class="bulletin-table" style="background: var(--neige);">
      <thead><tr><th>Cotisations patronales</th><th class="num">Base</th><th class="num">Taux</th><th class="num">Montant</th></tr></thead>
      <tbody>
        ${patronalRow('CNSS patronale', pat.base_cnss, PAYROLL_PARAMETERS.cnssEmployerTotal, pat.cnss, num)}
        ${patronalRow('&nbsp;&nbsp;dont prestations familiales et maternité', pat.base_cnss, PAYROLL_PARAMETERS.cnssPfEmployer, pat.cnss_pf, num)}
        ${patronalRow('&nbsp;&nbsp;dont accidents du travail et maladies professionnelles', pat.base_cnss, PAYROLL_PARAMETERS.cnssRiskEmployer, pat.cnss_atmp, num)}
        ${patronalRow('&nbsp;&nbsp;dont assurance vieillesse, invalidité, décès (VID)', pat.base_cnss, PAYROLL_PARAMETERS.cnssPensionEmployer, pat.cnss_vid, num)}
        ${patronalRow('CNAMGS patronale', pat.base_cnamgs, PAYROLL_PARAMETERS.cnamgsEmployer, pat.cnamgs, num)}
        ${pat.cfp ? patronalRow('CFP', pat.base_cnss, PAYROLL_PARAMETERS.cfpEmployer, pat.cfp, num) : ''}
        ${pat.fnh ? patronalRow('FNH', pat.base_cnss, PAYROLL_PARAMETERS.fnhEmployer, pat.fnh, num) : ''}
        ${pat.fgtls ? patronalRow('FGTLS', b.brut_imposable ?? 0, PAYROLL_PARAMETERS.fgtlsEmployer, pat.fgtls, num) : ''}
        <tr class="total"><td><strong>Total charges patronales</strong></td><td class="num">—</td><td class="num">—</td><td class="num"><strong>${num(pat.total)}</strong></td></tr>
        <tr><td>Brut versé (rappel)</td><td class="num">—</td><td class="num">—</td><td class="num">${num(brutVerse)}</td></tr>
        <tr class="total"><td><strong>Coût total employeur</strong></td><td class="num">—</td><td class="num">—</td><td class="num"><strong>${num(pat.cout_employeur)}</strong></td></tr>
      </tbody>
    </table>
    <div class="bulletin-footer">Document généré par Nexxus ERP · ${esc(t?.raison_sociale || t?.nom || '')} · ${esc(fmtPeriode(b.periode))}<br>Calcul réalisé avec les paramètres de paie configurés dans l'application${brandFooterTextHtml(theme)}</div>
  </div>`;
}
